import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FaPlus, FaEdit, FaTrash, FaFlask, FaSearch, FaUserMd, FaCalendarAlt, FaPaperclip, FaArrowLeft } from 'react-icons/fa';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import BottomNav from '../components/BottomNav';
import { useApp, useAutoRefresh, triggerRefresh } from '../context/AppContext';
import FileAttachment from '../components/FileAttachment';
import RecordCard from '../components/RecordCard';
import { getLabTests, createLabTest, updateLabTest, deleteLabTest, getPatients, getDoctors, getFileUrl, MAX_ATTACHMENT_SIZE, MAX_ATTACHMENT_SIZE_MESSAGE } from '../services/api';

const TESTS = ['Complete Blood Count', 'Lipid Profile', 'Blood Glucose', 'Thyroid Function', 'Urine Analysis', 'Liver Function', 'Kidney Function', 'X-Ray', 'ECG', 'Other'];
const EMPTY_FORM = { patientName: '', patientId: '', doctorId: '', testName: '', status: 'Pending', results: '', notes: '', date: new Date().toISOString().split('T')[0] };

const LabTests = () => {
  const { showToast, user } = useApp();
  const role = user?.role || 'Doctor';
  const [items, setItems] = useState([]);
  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewItem, setViewItem] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editId, setEditId] = useState(null);
  const [search, setSearch] = useState('');
  const [filterDate, setFilterDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [existingAttachments, setExistingAttachments] = useState([]);
  const fileRef = useRef();

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const [ltRes, patRes, docRes] = await Promise.allSettled([
        getLabTests(),
        role !== 'Patient' ? getPatients() : Promise.resolve({ data: { data: [] } }),
        role !== 'Doctor'  ? getDoctors() : Promise.resolve({ data: { data: [] } }),
      ]);
      setItems(ltRes.status === 'fulfilled' ? (ltRes.value.data?.data || []) : []);
      if (patRes.status === 'fulfilled') setPatients(patRes.value.data?.data || []);
      if (docRes.status === 'fulfilled') setDoctors(docRes.value.data?.data || []);
    } catch { setItems([]); }
    setLoading(false);
  }, [role]);

  useEffect(() => { fetchItems(); }, [fetchItems]);
  useAutoRefresh(fetchItems, 'labtests');

  // A doctor viewing their own Lab Tests page never fetches the full doctor
  // list (skipped above for role === 'Doctor') — every row here is their own
  // test order, so doctorId is always their own id. Resolve it directly
  // instead of falling through to the "#4" id fallback.
  const getDoctorName = (doctorId) => {
    if (!doctorId) return null;
    if (role === 'Doctor' && doctorId === user?.id) return user.name;
    return doctors.find(d => d.id === doctorId)?.name || `#${doctorId}`;
  };

  const getPatientPhoto = (item) => {
    if (role === 'Patient') return getFileUrl(user?.profileImage) || null;
    const p = patients.find(pt => String(pt.userId) === String(item.patientId) || String(pt.id) === String(item.patientId));
    return getFileUrl(p?.account?.profileImage) || null;
  };

  const buildFormData = () => {
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v ?? ''));
    if (editId) fd.append('existingAttachments', JSON.stringify(existingAttachments));
    selectedFiles.forEach(f => fd.append('files', f));
    return fd;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = (selectedFiles.length > 0 || editId) ? buildFormData() : form;
      if (editId) {
        const res = await updateLabTest(editId, payload);
        setItems(prev => prev.map(i => (i.id === editId || i._id === editId) ? (res.data?.data || { ...i, ...form }) : i));
        showToast('Test updated!', 'success');
      } else {
        const res = await createLabTest(payload);
        setItems(prev => [res.data?.data || res.data, ...prev]);
        showToast('Test added!', 'success');
        triggerRefresh('labtests');
      }
      setShowModal(false); setEditId(null); setSelectedFiles([]); setExistingAttachments([]);
      setForm({ ...EMPTY_FORM, date: new Date().toISOString().split('T')[0] });
    } catch (err) { showToast(err.message || 'Failed to save', 'error'); }
    setSaving(false);
  };

  const handleView = (item) => {
    setViewItem(item);
    setShowViewModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this lab test?')) return;
    try {
      await deleteLabTest(id);
      setItems(prev => prev.filter(i => i._id !== id && i.id !== id));
      showToast('Lab test deleted', 'error');
    } catch (err) { showToast(err.message || 'Delete failed', 'error'); }
  };

  const filtered = items.filter(i => {
    if (search) {
      const q = search.toLowerCase();
      const matched = i.patientName?.toLowerCase().includes(q) || i.testName?.toLowerCase().includes(q);
      if (!matched) return false;
    }
    if (filterDate && i.date !== filterDate) return false;
    return true;
  });

  if (showModal) {
    return (
      <div className="page-wrapper">
        <Header title={editId ? 'Edit Lab Test' : 'Add Lab Test'} />
        <Sidebar />
        <main className="page-content">
          <div className="page-header">
            <div>
              <h1><FaFlask style={{ color: 'var(--primary)', marginRight: 8 }} />{editId ? 'Edit Lab Test' : 'Add Lab Test'}</h1>
              <p>{editId ? 'Update test details' : 'Order a new lab test'}</p>
            </div>
            <button className="btn btn-outline" onClick={() => { setShowModal(false); setEditId(null); setSelectedFiles([]); setExistingAttachments([]); setForm({ ...EMPTY_FORM, date: new Date().toISOString().split('T')[0] }); }}>
              <FaArrowLeft /> Back
            </button>
          </div>
          <div className="card" style={{ padding: 24, maxWidth: 700, margin: '0 auto' }}>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Patient *</label>
                {patients.length > 0 ? (
                  <select required value={form.patientId} onChange={e => {
                    const pObj = patients.find(pt => String(pt.userId) === e.target.value || String(pt.id) === e.target.value);
                    const pid = pObj?.userId ? String(pObj.userId) : e.target.value;
                    setForm(prev => ({ ...prev, patientId: pid, patientName: pObj ? pObj.name : '' }));
                  }}>
                    <option value="">Select patient...</option>
                    {patients.map(p => <option key={p.id} value={p.userId || p.id}>{p.name}</option>)}
                  </select>
                ) : (
                  <input required value={form.patientName} onChange={e => setForm(p => ({ ...p, patientName: e.target.value }))} placeholder="Patient name" />
                )}
              </div>
              {role === 'Admin' && doctors.length > 0 && (
                <div className="form-group">
                  <label>Doctor</label>
                  <select value={form.doctorId} onChange={e => setForm(p => ({ ...p, doctorId: e.target.value }))}>
                    <option value="">Select doctor...</option>
                    {doctors.map(d => <option key={d.id} value={d.id}>{d.name}{d.specialization ? ` — ${d.specialization}` : ''}</option>)}
                  </select>
                </div>
              )}
              <div className="form-grid">
                <div className="form-group">
                  <label>Test Name *</label>
                  <select required value={form.testName} onChange={e => setForm(p => ({ ...p, testName: e.target.value }))}>
                    <option value="">Select test...</option>
                    {TESTS.map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div className="form-group"><label>Date</label><input type="date" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} /></div>
              </div>
              <div className="form-grid">
                <div className="form-group">
                  <label>Status</label>
                  <select value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value }))}>
                    <option>Pending</option><option>Completed</option><option>Cancelled</option>
                  </select>
                </div>
                <div className="form-group"><label>Results</label><input value={form.results} onChange={e => setForm(p => ({ ...p, results: e.target.value }))} placeholder="Test results..." /></div>
              </div>
              <div className="form-group">
                <label>Notes</label>
                <textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="Additional notes..." />
              </div>
              <div className="form-group">
                <label><FaPaperclip style={{ marginRight: 6 }} />Attachment (PDF, Image, Word)</label>
                {existingAttachments.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
                    {existingAttachments.map((att, i) => (
                      <FileAttachment key={i} url={att.url} filename={att.name} compact={true}
                        onRemove={() => setExistingAttachments(prev => prev.filter((_, idx) => idx !== i))} />
                    ))}
                  </div>
                )}
                <input ref={fileRef} type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={e => {
                  const files = Array.from(e.target.files);
                  if (files.some(f => f.size > MAX_ATTACHMENT_SIZE)) {
                    showToast(MAX_ATTACHMENT_SIZE_MESSAGE, 'error');
                    e.target.value = '';
                    return;
                  }
                  setSelectedFiles(files);
                }} style={{ padding: '8px 0', fontSize: 13 }} />
                {selectedFiles.length > 0 && <div style={{ fontSize: 12, color: 'var(--primary)', marginTop: 4 }}>{selectedFiles.map(f => f.name).join(', ')}</div>}
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <button type="button" className="btn btn-outline" style={{ flex: 1 }} onClick={() => { setShowModal(false); setEditId(null); setSelectedFiles([]); setExistingAttachments([]); setForm({ ...EMPTY_FORM, date: new Date().toISOString().split('T')[0] }); }}>Cancel</button>
                <button type="submit" className="btn btn-primary" style={{ flex: 1 }} disabled={saving}>{saving ? 'Saving...' : editId ? 'Update' : 'Add Test'}</button>
              </div>
            </form>
          </div>
        </main>
        <BottomNav />
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <Header title="Lab Tests" />
      <Sidebar />
      <main className="page-content">
        <div className="page-header">
          <div><h1><FaFlask style={{ color: 'var(--primary)', marginRight: 8 }} />Lab Tests</h1><p>{items.length} tests</p></div>
          {(role === 'Doctor' || role === 'Admin') && (
            <button className="btn btn-primary" onClick={() => { setEditId(null); setSelectedFiles([]); setExistingAttachments([]); setForm({ ...EMPTY_FORM, date: new Date().toISOString().split('T')[0] }); setShowModal(true); }}>
              <FaPlus /> Add
            </button>
          )}
        </div>
        <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
          <div className="search-bar" style={{ flex: 1, minWidth: 180, marginBottom: 0 }}>
            <FaSearch /><input placeholder="Search lab tests..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <input
            type="date"
            value={filterDate}
            onChange={e => setFilterDate(e.target.value)}
            style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13 }}
            title="Filter by date"
          />
          {filterDate && (
            <button className="btn btn-outline" style={{ padding: '8px 12px', fontSize: 12 }} onClick={() => setFilterDate('')}>
              Clear Filter
            </button>
          )}
        </div>

        {loading ? <div className="loading-container"><div className="spinner" /></div> :
          filtered.length === 0 ? <div className="empty-state"><FaFlask className="empty-state-icon" /><h3>No lab tests found</h3></div> :
          <div className="record-grid">
            {filtered.map(item => {
              const doctorName = getDoctorName(item.doctorId);
              const meta = [{ icon: <FaCalendarAlt size={11} />, text: item.date ? new Date(item.date).toLocaleDateString('en-IN') : '-' }];
              if (doctorName) meta.push({ icon: <FaUserMd size={11} />, text: doctorName });
              const hasAttachments = (Array.isArray(item.attachments) && item.attachments.length > 0) || item.file_url;
              return (
                <RecordCard
                  key={item._id || item.id}
                  avatarLetter={(item.patientName || '?')[0].toUpperCase()}
                  avatarImage={getPatientPhoto(item)}
                  title={item.patientName}
                  meta={meta}
                  items={[{
                    icon: <FaFlask size={13} />,
                    name: item.testName,
                    badges: [
                      <span key="status" className={`status-badge status-${item.status?.toLowerCase()}`}>{item.status}</span>,
                      item.results || 'Awaiting results',
                    ],
                  }]}
                  note={item.notes}
                  onView={() => handleView(item)}
                  actions={(role === 'Doctor' || role === 'Admin') && (
                    <>
                      <button className="action-btn action-btn-edit" onClick={() => {
                        setForm({ patientName: item.patientName, patientId: item.patientId || '', doctorId: item.doctorId || '', testName: item.testName, status: item.status, results: item.results || '', notes: item.notes || '', date: item.date });
                        setSelectedFiles([]);
                        setExistingAttachments(Array.isArray(item.attachments) && item.attachments.length > 0 ? item.attachments : (item.file_url ? [{ url: item.file_url, name: null }] : []));
                        setEditId(item._id || item.id); setShowModal(true);
                      }}><FaEdit /></button>
                      <button className="action-btn action-btn-delete" onClick={() => handleDelete(item._id || item.id)}><FaTrash /></button>
                    </>
                  )}
                >
                  {hasAttachments && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
                      {item.attachments && Array.isArray(item.attachments) && item.attachments.map((att, i) => (
                        <FileAttachment key={i} url={att.url} filename={att.name} compact={true} />
                      ))}
                      {(!Array.isArray(item.attachments) || item.attachments.length === 0) && item.file_url && (
                        <FileAttachment url={item.file_url} compact={true} />
                      )}
                    </div>
                  )}
                </RecordCard>
              );
            })}
          </div>
        }
      </main>
      <BottomNav />

      {showViewModal && viewItem && (
        <div className="side-panel-overlay" onClick={e => e.target === e.currentTarget && setShowViewModal(false)}>
          <div className="side-panel">
            <div className="modal-header">
              <h2>Lab Test Details</h2>
              <button className="modal-close" onClick={() => setShowViewModal(false)}>×</button>
            </div>
            <div style={{ marginBottom: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 15, paddingBottom: 10, borderBottom: '1px solid #eee' }}>
                <div>
                  <div style={{ fontSize: 12, color: 'var(--text-light)', textTransform: 'uppercase', letterSpacing: 1 }}>Patient</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--primary)' }}>{viewItem.patientName}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 12, color: 'var(--text-light)', textTransform: 'uppercase', letterSpacing: 1 }}>Date</div>
                  <div style={{ fontSize: 15, fontWeight: 600 }}>{viewItem.date ? new Date(viewItem.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : '-'}</div>
                </div>
              </div>

              <div style={{ marginBottom: 15 }}>
                <div style={{ fontSize: 12, color: 'var(--text-light)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Test Information</div>
                <div style={{ background: '#f8f9fa', padding: 12, borderRadius: 10, borderLeft: '4px solid var(--primary)' }}>
                  <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8 }}>{viewItem.testName}</div>
                  <div style={{ display: 'flex', gap: 20, fontSize: 14 }}>
                    <div><strong style={{ color: 'var(--text-medium)' }}>Status:</strong> <span className={`status-badge status-${viewItem.status?.toLowerCase()}`}>{viewItem.status}</span></div>
                    {viewItem.doctorId && <div><strong style={{ color: 'var(--text-medium)' }}>Doctor:</strong> Dr. {getDoctorName(viewItem.doctorId)}</div>}
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: 15 }}>
                <div style={{ fontSize: 12, color: 'var(--text-light)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Results & Notes</div>
                <div style={{ background: '#e3f2fd', padding: 12, borderRadius: 10, marginBottom: 8 }}>
                  <strong style={{ color: 'var(--text-dark)', display: 'block', marginBottom: 4 }}>Results:</strong>
                  <div style={{ color: 'var(--text-medium)' }}>{viewItem.results || 'Awaiting results...'}</div>
                </div>
                {viewItem.notes && (
                  <div style={{ background: '#fff9c4', padding: 12, borderRadius: 10, color: '#5d4037', fontSize: 14 }}>
                    <strong style={{ display: 'block', marginBottom: 4 }}>Notes:</strong>
                    {viewItem.notes}
                  </div>
                )}
              </div>

              {((Array.isArray(viewItem.attachments) && viewItem.attachments.length > 0) || viewItem.file_url) && (
                <div>
                  <div style={{ fontSize: 12, color: 'var(--text-light)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Attachments</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                    {Array.isArray(viewItem.attachments) && viewItem.attachments.map((att, i) => (
                      <FileAttachment key={i} url={att.url} filename={att.name} />
                    ))}
                    {!Array.isArray(viewItem.attachments) && viewItem.file_url && <FileAttachment url={viewItem.file_url} />}
                  </div>
                </div>
              )}
            </div>
            <button className="btn btn-primary" style={{ width: '100%' }} onClick={() => setShowViewModal(false)}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LabTests;
